import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDocumentArrowRightOutlineIcon from 'mdi-react/FileDocumentArrowRightOutlineIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'

import { getReportExportUsingAPI } from '../exportFormats'
import { useReportsContext } from '../index.context'

import { getISODate } from './helpers'
import { OtherOptions } from './OtherOptions'

import type { ReportFilters, ReportSetupExportSettings } from '../../resources'
import adminResources from '../../resources'
import type { ExportFiltersType, ExportOptionsType } from '../../resources/reports/types'
import { EXPORT_OPTIONS } from '../../resources/reports/types'

export const SalaryDeductionDeviceOptions: FC<{
  title: string
  filters: ReportFilters
  onExportOptionClick: (id: ExportOptionsType) => void
  reportSetupExportSettings?: ReportSetupExportSettings
}> = ({ title, filters, reportSetupExportSettings }) => {
  const translate = useTranslation()
  const profile = useProfile()
  const { data: reports } = useQuery(queries.adminSalaryDeductionDeviceReports(filters), () =>
    adminResources.getSalaryDeductionDeviceReports(filters)
  )
  const isThreeStepIT = useIsThreeStepIT()

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const { customerSpecificReports } = useReportsContext()

  const { fromDate, toDate } = filters
  if (!fromDate || !toDate) throw Error('Missing date range filters!')

  const reportsData = reports.map(report => ({
    name: report.name,
    employeeNo: report.employeeNo,
    departmentCode: report.departmentCode,
    department: report.departmentName,
    assetDescription: report.model,
    orderDate: getISODate(report.orderDate),
    lastUpdated: getISODate(report.orderUpdate),
    deliveryDate: getISODate(report.deliveryDate),
    downpayments: report.downPayments,
    totalAmount: report.totalAmount,
    coverAmount: report.coverAmount,
    salaryDeductionAmount: report.amount,
    isBuyout: report.isBuyout ? translate('Yes') : translate('No'),
    companyName: report.customerName,
    accountingCode: report.accountingCode,
    project: report.projectDevice,
    departmentOverride: report.departmentOverrideDevice || '',
    currency: profile?.currency || '',
  }))

  const columnFormats = {
    orderDate: 'date',
    orderUpdate: 'date',
    deliveryDate: 'date',
    downpayments: 'amount',
    coverAmount: 'amount',
    totalAmount: 'amount',
    salaryDeductionAmount: 'amount',
    lastUpdated: 'date',
  }

  const companyNames = reports.map(report => report.customerName)
  let companyName = ''
  if (companyNames.length > 0) {
    companyName = companyNames[0]
  }

  const handleReportExport = (formatName: ExportFiltersType['formatName']) => {
    getReportExportUsingAPI({
      reportName: 'ssd-order',
      formatName,
      isGlobal: filters.isGlobal,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerId: filters.selectedCustomer,
    })
  }

  const customerSpecificReport = customerSpecificReports.find(
    element => element.report === filters.selectedReportType
  )

  return (
    <>
      <OtherOptions
        title={title}
        reportsData={reportsData}
        columnFormats={columnFormats}
        filters={filters}
      />
      {reportSetupExportSettings && (
        <div className="grid grid-cols-2">
          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            id={EXPORT_OPTIONS.VISMA_LONN}
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN)}
          >
            <Translation id="Visma Lønn V1" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            id={EXPORT_OPTIONS.VISMA_LONN_V2}
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN_V2)}
          >
            <Translation id="Visma Lønn V2" />
          </Button>

          <Button
            Icon={<FileDocumentArrowRightOutlineIcon />}
            id={EXPORT_OPTIONS.VISMA_LONN_V3}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN_V3)}
          >
            <Translation id="Visma Lønn V3" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.VISMA_PAYROLL}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_PAYROLL)}
          >
            <Translation id="Visma Payroll" />
          </Button>

          <Button
            Icon={<FileDocumentArrowRightOutlineIcon />}
            id={EXPORT_OPTIONS.HULDT_OG_LILLEVIK}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.HULDT_OG_LILLEVIK)}
          >
            <Translation id="Huldt & Lillevik" />
          </Button>

          <Button
            id={EXPORT_OPTIONS.HULDT_OG_LILLEVIK_SDV}
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.HULDT_OG_LILLEVIK_SDV)}
          >
            <Translation id="Huldt & Lillevik Telenor SDV" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.SAP}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SAP)}
          >
            <Translation id="SAP" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.SAP_V2}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SAP_V2)}
          >
            <Translation id="SAP v2" />
          </Button>

          <Button
            id={EXPORT_OPTIONS.TRIPLETEX}
            Icon={<FileDocumentArrowRightOutlineIcon />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.TRIPLETEX)}
          >
            <Translation id="Tripletex" />
          </Button>

          <Button
            id={EXPORT_OPTIONS.SD_WORKS_ADITRO}
            Icon={<FileDocumentArrowRightOutlineIcon />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SD_WORKS_ADITRO)}
          >
            <Translation id="SD Works" />
          </Button>

          {customerSpecificReport && (
            <Button
              id={EXPORT_OPTIONS.CUSTOMER_SPECIFIC}
              Icon={<TrayArrowDownIcon />}
              onClick={() => handleReportExport(EXPORT_OPTIONS.CUSTOMER_SPECIFIC)}
            >
              {customerSpecificReport.friendlyName ?? companyName}
            </Button>
          )}
        </div>
      )}
    </>
  )
}
